import { useEffect } from 'react';
import axios from 'axios';
import { createDocEntryStore } from './docStore';
import { currentRun, docMode, docReady, writeState } from './docState';
import { stopRuns } from './runHalt';

// WHAT ANOTHER READER IS DOING TO THIS MANUAL RIGHT NOW.
//
// A phase's run happens in the browser that pressed the button: it opens the PDF with
// pdf.js, renders the pages and makes one model call per step, in a loop in that tab. Every
// other tab is blind to it. `busy`, `running` and `progress` are in that tab's memory and
// are deliberately never persisted (see stepsRun's Snapshot) — a run does not survive a
// reload of the tab running it, let alone reach a different one.
//
// So a second tab showed a phase with no results and nothing to explain them, which is the
// worst reading available: a full re-run empties last run's answers before it refills them,
// so what that tab hydrates is a phase that looks like it has NEVER been run, for as long
// as the run takes. Empty bar, "Analyze every step", no spinner.
//
// One key says otherwise. The running tab writes its progress into it and clears it at the
// end; every other reader polls it and draws the phase as working.

// The key, in the same shape as every other one this app stores — `<base>.<docId>`, in the
// document's current run. One per document and not one per phase: two phases of one manual
// are not run at once (the chain waits), and a single key is a single answer to "is
// anything happening here", which is the question every reader has.
const workingKey = (docId: string) => `notice.working.${docId}`;

// WHAT A RUNNING TAB SAYS ABOUT ITSELF. The phase number, so a reader knows which card to
// light, and how far along it is — the same two numbers the running tab's own progress bar
// draws. No clock: a timestamp written by one browser means nothing to another, which is
// what the server's `age` is for (see the /working route).
export type Working = { phase: number; done: number; total: number };

// WHICH PHASES REPORT THEMSELVES, by the number the reader gives them — named once, because
// the tab that runs and the tabs that watch compare this number and must agree on it.
export const STEPS_PHASE = 4;
export const HOLES_PHASE = 5;

// HOW LONG A BEAT IS GOOD FOR. A tab that crashes or is closed mid-run leaves its flag set
// with nobody to clear it, so the flag alone means "running" forever. The tab beats every
// BEAT_MS while it works; a reader that finds a beat older than this treats the run as
// gone.
//
// Wide, because the beat competes with the work: a step's call can take a minute on a busy
// manual, and the beat rides the same debounced save queue as the answers. A window too
// narrow makes a live run flicker off and on, which is a worse lie than a dead one showing
// for another half minute.
const STALE_S = 120;
export const BEAT_MS = 20_000;
// How often a reader that is NOT running asks. Slow on purpose: what it is watching is a
// phase that takes minutes, and the answer is one small row.
const POLL_MS = 15_000;

// SAID BY THE TAB DOING THE WORK. Written through the ordinary state path — debounced,
// merged server-side, in the current run — so this costs no request of its own: it rides
// with the answers the run is already saving.
export const setWorking = (docId: string, value: Working | null) => {
  if (!docReady(docId)) return;
  writeState(docId, workingKey(docId), value);
};

type Snapshot = {
  // Null when nothing is running anywhere, or when the last thing that claimed to be has
  // gone quiet. Never this tab's own run: that tab knows its own state and does not need to
  // be told about it a poll later.
  elsewhere: Working | null;
};

const store = createDocEntryStore<Snapshot>(() => ({ snapshot: { elsewhere: null } }));

// ONE POLL PER DOCUMENT however many components ask — the progress bar and the phase's own
// run button both do — so two readers of one answer are not two requests.
//
// Counted, and not a "someone is already polling" flag. With a flag the FIRST component to
// unmount stopped the timer and the second went on reading a store nothing refreshed, which
// is the worst kind of stale: a phase frozen mid-run on screen with no one asking whether it
// still is. It is also what React's double-mount does to every effect in development.
const pollers = new Map<string, { count: number; stop: () => void }>();

async function readWorking(docId: string) {
  try {
    const { data } = await axios.get<{ working: Working | null; age: number | null }>(
      `/api/notice/doc/${encodeURIComponent(docId)}/working`,
      { params: { mode: docMode(), run: currentRun(docId) } },
    );
    const live = data.working && data.age != null && data.age <= STALE_S ? data.working : null;
    const now = store.get(docId).elsewhere;
    // Compared by value: the poll returns the same object shape every time, and patching
    // on every tick would re-render the whole progress bar four times a minute for nothing.
    if (now?.phase === live?.phase && now?.done === live?.done && now?.total === live?.total) {
      return;
    }
    store.patch(docId, { elsewhere: live });
  } catch {
    // Left as it was. An unreachable server is not evidence that a run stopped, and the
    // beat's own staleness is what ends the claim if it really did.
  }
}

// WHAT SOMEBODY ELSE IS RUNNING, or null. Polled while this document is open and this tab
// is visible — a hidden tab is not being read, and the answer is re-asked the moment it
// comes back, so nothing is lost by not asking meanwhile.
export function useWorkingElsewhere(docId: string): Working | null {
  const { elsewhere } = store.useSnapshot(docId);
  useEffect(() => {
    if (!docId) return undefined;
    const live = pollers.get(docId);
    if (live) {
      live.count += 1;
    } else {
      const tick = () => {
        if (document.visibilityState === 'visible') void readWorking(docId);
      };
      tick();
      const timer = window.setInterval(tick, POLL_MS);
      document.addEventListener('visibilitychange', tick);
      pollers.set(docId, {
        count: 1,
        stop: () => {
          window.clearInterval(timer);
          document.removeEventListener('visibilitychange', tick);
        },
      });
    }
    return () => {
      const held = pollers.get(docId);
      if (!held) return;
      held.count -= 1;
      if (held.count > 0) return;
      held.stop();
      pollers.delete(docId);
    };
  }, [docId]);
  return elsewhere;
}

// A STOP ASKED FROM ANOTHER TAB. The run lives in the tab that pressed Run (see above), so a tab
// that only watches it had no stop to offer: what it can do is say so, in a key of its own, and
// the running tab, asking every few seconds, halts itself as its own Stop would (see runHalt) —
// between calls, keeping what has landed.
const stopKey = (docId: string) => `notice.stopAsked.${docId}`;
const STOP_POLL_MS = 4_000;
export const askStopElsewhere = (docId: string) => {
  if (!docReady(docId)) return;
  writeState(docId, stopKey(docId), `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
};
const readStop = async (docId: string): Promise<{ stop: unknown } | null> => {
  try {
    const { data } = await axios.get<{ stop?: unknown }>(
      `/api/notice/doc/${encodeURIComponent(docId)}/working`,
      { params: { mode: docMode(), run: currentRun(docId) } },
    );
    return { stop: data.stop ?? null };
  } catch {
    return null;
  }
};
// WATCHED BY THE RUNNING TAB, from its start: a token other than the one it found then is a stop.
// Returns what ends the watch.
export function watchRemoteStop(docId: string): () => void {
  let first: unknown;
  let known = false;
  let over = false;
  const tick = async () => {
    const got = await readStop(docId);
    if (!got || over) return;
    if (!known) { first = got.stop; known = true; return; }
    if (got.stop !== first) {
      over = true;
      stopRuns(docId);
    }
  };
  void tick();
  const timer = window.setInterval(() => void tick(), STOP_POLL_MS);
  return () => { over = true; window.clearInterval(timer); };
}
